import { useEffect, useRef } from "react"
import maplibregl, { Map, Marker } from "maplibre-gl"
import "maplibre-gl/dist/maplibre-gl.css"
import { style } from "./data/data"
import { MapState, POI, useMapStore } from "@state/mapStore"
import styled from "styled-components"
import { useStore } from "@state/store"
import { createRoot, Root } from "react-dom/client"
import { MapSidebar } from "./MapSidebar"

type OfflineMapProps = {
  pois: POI[]
  mainMarker: POI
  categories: string[]
  withDirections: boolean
  panelRef?: React.RefObject<HTMLDivElement>
}

type CustomMarkerProps = {
  index: number
}

const CustomMarker = ({ index }: CustomMarkerProps) => (
  <MarkerContainer>
    <BlurBackground />
    <MarkerText>{index + 1}</MarkerText>
  </MarkerContainer>
)

const OfflineMap: React.FC<OfflineMapProps> = ({
  pois,
  mainMarker,
  categories,
  withDirections,
  panelRef,
}) => {
  const contentControls = useStore((s) => s.contentControls)
  const mapContainer = useRef<HTMLDivElement>(null)
  const mapInstance = useRef<Map | null>(null)
  const markersRef = useRef<Marker[]>([])
  const rootsRef = useRef<Root[]>([])
  const { travelMode, activePOI, setActivePOI } = useMapStore(
    (state: MapState) => ({
      travelMode: state.travelMode,
      activePOI: state.activePOI,
      setActivePOI: state.setActivePOI,
    }),
  )

  const categoryIndex = contentControls?.index
  const currentCategory = categories[categoryIndex]

  useEffect(() => {
    if (activePOI) drawLineAndZoom(activePOI)
  }, [activePOI, travelMode])

  useEffect(() => {
    if (!mapContainer.current || mapInstance.current) return

    mapInstance.current = new maplibregl.Map({
      container: mapContainer.current,
      style: style as maplibregl.StyleSpecification,
      center: [mainMarker.coordinates.lng, mainMarker.coordinates.lat],
      minZoom: 11,
      zoom: 13,
    })

    return () => {
      markersRef.current.forEach((marker) => marker.remove())
      markersRef.current = []

      if (mapInstance.current) {
        mapInstance.current.remove()
        mapInstance.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!mapInstance.current) return

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.remove())
    markersRef.current = []

    // Add main marker
    const mainMarkerEl = document.createElement("img")
    mainMarkerEl.src = mainMarker.iconPath.publicURL
    mainMarkerEl.style.width = "75px"
    mainMarkerEl.style.height = "75px"
    mainMarkerEl.style.cursor = "default"

    const mainMapMarker = new Marker({
      element: mainMarkerEl,
      anchor: "bottom",
    })
      .setLngLat({
        lng: mainMarker.coordinates.lng,
        lat: mainMarker.coordinates.lat,
      })
      .addTo(mapInstance.current)

    markersRef.current.push(mainMapMarker)

    pois.forEach((poi, i) => {
      if (poi.category === currentCategory) {
        const markerEl = document.createElement("div")
        const root = createRoot(markerEl)
        root.render(<CustomMarker index={i} />)

        const marker = new Marker({
          element: markerEl,
          anchor: "bottom",
        })
          .setLngLat({
            lng: poi.coordinates.lng,
            lat: poi.coordinates.lat,
          })
          .addTo(mapInstance.current!)

        markerEl.addEventListener("click", () => {
          drawLineAndZoom(poi)
          setActivePOI(poi)
        })
        markersRef.current.push(marker)
      }
    })

    // Cleanup function
    return () => {
      markersRef.current.forEach((marker) => marker.remove())
      rootsRef.current.forEach((root) => root.unmount())
      markersRef.current = []
      rootsRef.current = []
    }
  }, [pois, mapInstance.current, currentCategory])

  const drawLineAndZoom = (poi: POI) => {
    const routeCoordinates = poi?.routes?.[travelMode?.toLowerCase()].path
    if (!routeCoordinates) {
      console.error(`No route found for POI ${poi?.id}`)
      return
    }

    const geojson: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: routeCoordinates,
          },
          properties: {},
        },
      ],
    }

    if (mapInstance.current?.getSource("route")) {
      //@ts-ignore
      mapInstance.current.getSource("route").setData(geojson)
    } else {
      mapInstance.current?.addSource("route", {
        type: "geojson",
        data: geojson,
      })
      mapInstance.current?.addLayer({
        id: "route",
        type: "line",
        source: "route",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#c1bca8",
          "line-width": 6,
          "line-opacity": 0.8,
        },
      })
    }

    const bounds: maplibregl.LngLatBounds = routeCoordinates.reduce(
      (bounds: maplibregl.LngLatBounds, coord: [number, number]) => {
        return bounds.extend(new maplibregl.LngLat(coord[0], coord[1]))
      },
      new maplibregl.LngLatBounds(
        routeCoordinates[0] as [number, number],
        routeCoordinates[0] as [number, number],
      ),
    )

    mapInstance.current?.fitBounds(bounds, {
      padding: 120,
      duration: 1000,
      animate: true,
      curve: 1,
    })
  }

  return (
    <div className="map-wrapper">
      <MapSidebar
        categories={categories}
        withDirections={withDirections}
        mainMarker={mainMarker}
        panel={panelRef}
        pois={pois}
      />
      <div
        id="map"
        ref={mapContainer}
        style={{ width: "100%", height: "100vh" }}
      />
    </div>
  )
}

export default OfflineMap

const MarkerContainer = styled.div`
  width: 60px;
  height: 60px;
  cursor: pointer;
  position: relative;
  border-radius: 4px;
  overflow: hidden;
`

const BlurBackground = styled.div`
  position: absolute;
  inset: -10px;
  background: #754248;
  border-radius: 4px;
  transform: scale(1.1);
`

const MarkerText = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  font-size: 16px;
  color: #fff;
  font-family: ThirdFont-Regular;
`
